import { useLocalization, useSelectedOrdersRows } from 'src/Hooks';
import { useSelector } from 'react-redux';
import { globalSelectors } from 'src/Redux/Global/selectors';
import { useMemo } from 'react';
import { localizationUtils } from 'src/Utils/localizationUtils';
import { FieldState } from 'src/Types';
import { orderTableRowUtils } from 'src/Types/OrderTableRowUtils';

export const useCancelInvoiceTooltipMessage = (): FieldState => {
  const {
    models: {
      OrderTableRow: { CancelInvoiceNumber, parts, CommonErrors },
    },
  } = useLocalization();

  const { orderRows, isPurchasesPage } = useSelectedOrdersRows();
  const localization = useSelector(globalSelectors.localization);

  return useMemo(() => {
    const result = orderRows.reduce(
      (res, current) => {
        if (current.currentCancelInvoiceCreatedTime) {
          res.withCancelInvoiceNumbers.add(current.currentCancelInvoiceNumber ?? '');
        }

        if (orderTableRowUtils.isOrderIsAmazonPending(current)) {
          res.amazonInProcessOrders.add(current.userOrderId.toString());
        }
        return res;
      },
      {
        withCancelInvoiceNumbers: new Set<string>(),
        amazonInProcessOrders: new Set<string>(),
      },
    );

    const withCancelInvoiceNumbers = [...result.withCancelInvoiceNumbers];
    const amazonInProcessOrders = [...result.amazonInProcessOrders];

    const isEverySalesWithInvoices = orderRows.every((sale) => sale.invoices?.length);

    let disabledDocumentMessage: string | undefined = undefined;
    const isIconDisabled = Boolean(withCancelInvoiceNumbers.length || amazonInProcessOrders.length);

    if (withCancelInvoiceNumbers.length) {
      disabledDocumentMessage = localizationUtils.insertTranslatedParamsToString(
        CancelInvoiceNumber.DisableHeaderDocumentMessage,
        [withCancelInvoiceNumbers.join(', '), withCancelInvoiceNumbers.length],
        parts,
        localization,
      );
    } else if (!isEverySalesWithInvoices) {
      disabledDocumentMessage = CancelInvoiceNumber.NoInvoicesCreatedYetMessage;
    } else if (amazonInProcessOrders.length) {
      disabledDocumentMessage = CommonErrors.AmazonOrderPending.DisableHeaderDocumentMessage;
    } // if

    return {
      value: '',
      isDocumentIconDisabled: isIconDisabled || !isEverySalesWithInvoices || isPurchasesPage,
      disabledDocumentMessage,
    };
  }, [localization, orderRows, isPurchasesPage]);
};
